import { EventEmitter } from 'events';
import { SessionState, WebCamState } from './Enums';
import Environment from './Environment';
import ErrorService from './ErrorService';
import ProxyRecorder from './ProxyRecorder';
import { Reasons } from './Reasons/Reasons';
import QualityCheckScreen from './Screens/QualityCheckScreen';
import WebCamReallowScreen from './Screens/WebCamReallowScreen';
import SessionService from './SessionService';
import FaceService from './FaceService';
import Test from './Test';
import { UiService } from './UiService';

export default class WebCamAccessManager extends EventEmitter {
    /**
     * WebCamAccessManager constructor
     * @param {Environment} environment
     * @param {SessionService} sessionService
     * @param {FaceService} faceService
     * @param {ProxyRecorder} proxyRecorder
     * @param {UiService} uiService
     * @param {object} options
     * @param {object} jsModuleSettings
     * @param {ErrorService} errorService
     * @param {Test} test
     */
    constructor(
        environment,
        sessionService,
        faceService,
        proxyRecorder,
        uiService,
        options,
        jsModuleSettings,
        errorService,
        test
    ) {
        super();

        this.environment = environment;
        this.sessionService = sessionService;
        this.faceService = faceService;
        this.proxyRecorder = proxyRecorder;
        this.uiService = uiService;
        this.errorService = errorService;
        this.test = test;

        this.options = options;
        this.jsModuleSettings = jsModuleSettings;

        this.setUpWebCamResolve = null;
        this.webCamProcessState = null;
        this.permissionAllowed = false;
    }

    async showQualityCheckScreen() {
        const qualityCheckScreen = new QualityCheckScreen(
            this.uiService,
            this.proxyRecorder,
            this.sessionService,
            this.faceService,
            this.environment,
            this.options,
            this.jsModuleSettings,
            this.errorService,
            this.test
        );
        return await qualityCheckScreen.display();
    }

    async askWebcamPermissionsWithUI() {
        let webcamAllowed = await this.startAskWebcamPermissions();

        if (webcamAllowed === WebCamState.allowed) {
            return webcamAllowed;
        }

        // display reallow if needed
        if (webcamAllowed === WebCamState.autoDenied) {
            const reallowScreen = new WebCamReallowScreen(this.uiService, this.environment);

            this.broadcastWebCamStateChange(WebCamState.reAllowDisplayed);

            webcamAllowed = await reallowScreen.display();

            if (webcamAllowed === WebCamState.retryRequest) {
                this.broadcastWebCamStateChange(WebCamState.retryRequest);

                return await this.askWebcamPermissionsWithUI();
            }
        }

        return WebCamState.denied;
    }

    // TODO: return WebCamState instead of boolean. After that, no need to track state.
    async startAskWebcamPermissions(isReallow = false) {
        this.permissionAsked = true;

        if (this.environment.environmentCheckerResult?.error) {
            const failedData = {
                reason: this.environment.environmentCheckerResult.error.reason.getUIMessage()
            };

            this.broadcastWebCamStateChange(WebCamState.failed, failedData);

            return WebCamState.failed;
        }

        const data = {
            data: {
                camera: '',
                mic: ''
            }
        };

        this.broadcastWebCamStateChange(WebCamState.startAskPermissions, data);

        const { collected, fail, declined, requesting_access_to_webcam } = SessionState;

        const lastSessionStateId = this.sessionService.lastSessionStateId;
        const isSessionOver =
            lastSessionStateId === collected && lastSessionStateId === fail && lastSessionStateId === declined;

        if (isReallow || !isSessionOver) {
            if (lastSessionStateId === declined) {
                this.sessionService.lastSessionStateId = null;
                this.sessionService.changeState(requesting_access_to_webcam, null, 're-ask permissions');
            }

            this.sessionService.changeState(requesting_access_to_webcam, Reasons.environment_checks_succeeded.id, null);

            try {
                await this.proxyRecorder.askPermissions();

                this.permissionAllowed = true;

                this.webCamProcessState = WebCamState.allowed;

                const data = {
                    data: {
                        camera: {},
                        mic: {}
                    }
                };

                this.broadcastWebCamStateChange(WebCamState.allowed, data);

                return WebCamState.allowed;
            } catch (e) {
                return this.onFailPermissionRequest(e);
            }
        } else {
            this.broadcastWebCamStateChange(WebCamState.failed, {
                reason: `Session already has been terminated/finished and has state '${this.sessionService.lastSessionStateId}' and reason '${this.sessionService.lastReasonId}:"${this.sessionService.lastReasonMessage}'`
            });

            return WebCamState.failed;
        }
    }

    onFailPermissionRequest(e) {
        if (!e.devices) {
            this.webCamProcessState = WebCamState.failed;
        }

        let data = {};

        if (e.devices.video.permissionState === 4 && jsModuleSettings.showReAllow) {
            this.autoDenyDetected = true;

            this.webCamProcessState = WebCamState.autoDenied;
        } else {
            this.webCamProcessState = WebCamState.denied;

            data = {
                isAutoDeny: this.autoDenyDetected,
                devices: (e || {}).devices,
                environment: this.environment.detectorResult
            };
        }

        this.broadcastWebCamStateChange(this.webCamProcessState, data);
        return this.webCamProcessState;
    }

    async broadcastWebCamStateChange(state, data = {}, withoutDevices) {
        data.state = state;

        if (!withoutDevices && !(data || {}).devices) {
            data.devices = await this.proxyRecorder.getCurrentDevices();
        }

        this.emit('webcamStateChanged', {
            data: data
        });
    }
}
